import { deleteField } from "firebase/firestore";

import {
  CHAPTER_LIMIT,
  HIGHLIGHT_BLURB_MAX,
  HIGHLIGHT_TITLE_MAX,
} from "../events/landing";

/** What the agenda form edits for a session promoted to a landing chapter. */
export type HighlightForm = { on: boolean; title: string; blurb: string };

export type HighlightProblems = { title?: string; blurb?: string };

type HighlightStored = {
  highlight?: boolean | null;
  highlightTitle?: string | null;
  highlightBlurb?: string | null;
  /** Any session shape: the rest of an agenda item is none of this file's business. */
  [other: string]: unknown;
};

export function highlightFormFrom<T extends HighlightStored>(
  item: T | null | undefined,
): HighlightForm {
  return {
    on: item?.highlight === true,
    title: item?.highlightTitle ?? "",
    blurb: item?.highlightBlurb ?? "",
  };
}

/**
 * What the rules would refuse. The texts are not saved while the switch is
 * off, so they cannot be wrong then.
 */
export function highlightProblems(form: HighlightForm): HighlightProblems {
  if (!form.on) return {};
  const problems: HighlightProblems = {};
  if (form.title.trim().length > HIGHLIGHT_TITLE_MAX)
    problems.title = `The chapter title must be ${HIGHLIGHT_TITLE_MAX} characters or fewer.`;
  if (form.blurb.trim().length > HIGHLIGHT_BLURB_MAX)
    problems.blurb = `The chapter text must be ${HIGHLIGHT_BLURB_MAX} characters or fewer.`;
  return problems;
}

/**
 * The three fields as the rules take them.
 *
 * On create the keys are left out unless they carry something. On update an
 * absent key means "leave as it is": a switch turned off is removed with
 * deleteField() and the texts are not sent, while a text emptied by hand is
 * removed with deleteField() too.
 */
export function highlightFields(
  form: HighlightForm,
  mode: "create" | "update",
): Record<string, unknown> {
  const title = form.title.trim();
  const blurb = form.blurb.trim();
  if (mode === "create") {
    if (!form.on) return {};
    return {
      highlight: true,
      ...(title ? { highlightTitle: title } : {}),
      ...(blurb ? { highlightBlurb: blurb } : {}),
    };
  }
  // Off clears the switch alone. The texts stay in the document, harmless
  // because a chapter renders only for a session with highlight true, so an
  // accidental toggle does not destroy what somebody typed.
  if (!form.on) return { highlight: deleteField() };
  return {
    highlight: true,
    highlightTitle: title || deleteField(),
    highlightBlurb: blurb || deleteField(),
  };
}

/**
 * A warning, never a block, when saving would leave more than three sessions
 * highlighted: the landing page takes only the first three in time order, so
 * the others would be marked and not shown. Counted without the session being
 * edited, which is replaced by what the form says.
 */
export function highlightChapterWarning(
  items: readonly { id: string; highlight?: boolean | null }[],
  editingId: string | null,
  on: boolean,
): string | null {
  if (!on) return null;
  const others = items.filter(
    (item) => item.id !== editingId && item.highlight === true,
  ).length;
  const total = others + 1;
  if (total <= CHAPTER_LIMIT) return null;
  return `${total} sessions would be highlighted. Only the first three, in time order, appear as chapters on the landing page.`;
}
